import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { MainNav } from "./MainNav";
import { Sidebar } from "./Sidebar";
import { MobileNav } from "./MobileNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { BellIcon } from "lucide-react";
import { api } from "@/lib/api";

export const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    api.get("/notifications/unread-count")
      .then(res => setUnreadCount(res.count ?? 0))
      .catch(() => {});
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col bg-neutral-50 dark:bg-neutral-900">
      {/* Header — full-width, fixed on top, above everything */}
      <header className="fixed top-0 left-0 right-0 z-[120] h-16 border-b bg-white/90 dark:bg-neutral-900/90 backdrop-blur-sm">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center flex-1">
            {!isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 mr-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            )}
            <div className="w-full">
              <MainNav />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/notifications")}
              className="relative p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              aria-label="Notifications"
            >
              <BellIcon className="h-5 w-5 text-neutral-600 dark:text-neutral-300" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <ThemeToggle />
            {isMobile && <MobileNav />}
          </div>
        </div>
      </header>

      {/* Spacer to push content below the fixed header */}
      <div className="h-16 flex-shrink-0" />

      {/* Sidebar + Main content area */}
      <div className="flex flex-1 relative">
        {!isMobile && (
          <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
        )}
        <main className={`flex-1 overflow-y-auto p-6 md:p-8 transition-all duration-300 ${
          !isMobile && sidebarOpen ? 'ml-64' : !isMobile ? 'ml-20' : 'ml-0'
        }`}>
          <div className="mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
